import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Pressable,
  View,
  Platform,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  useWindowDimensions
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Drawer } from 'react-native-drawer-layout'
import { Switch } from 'react-native-paper'
import { create } from 'zustand'
import { useThrottledCallback } from 'use-debounce'
import * as Clipboard from 'expo-clipboard'

import { useRouter } from 'expo-router'
import { useQuery } from 'convex/react'
import { DS } from '@/design-system'
import { useStore, rootStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { translate } from '../../i18n'
import { Button } from '../../components/ANewDesign/Button'
import { DrawerItem } from '../../components/ANewDesign/DrawerItem/DrawerItem'
import { DrawerCardColors } from '../../components/ANewDesign/DrawerCardColors/DrawerCardColors'
import { useCheatSheet } from '../../components/CheatSheet/useCheatSheet'
import { useSupportFormModal } from '../../components/Support/useSupportFormModal'
import { color, fontFamily } from '../../theme'
import { BUILD_VERSION_FORMATTED_TEXT } from '../../utils/buildVersionText'
import { GameStates, Player } from '@ppwr/poker'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { useToastStore } from '@/navigation/toast'
import { promptShareUrl } from '../../utils/shareUrl'
import { useGamePanelsStore } from './useGamePanelsStore'
import { ShareCodeSheet } from '../../components/ShareCodeSheet'
import { GameModal } from './Modals/GameModalContainer'
import { GameSettingsPanel } from './GameSettingsPanel'
import { useGameContext } from './GameContext'
import { useGameState } from './useGameState'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { useActionAdvisor } from './useActionAdvisor'
import { ACTION_ADVISOR_COPY } from './actionAdvisor.constants'
import { useActionAdvisorStore } from './useActionAdvisorStore'

const isAndroid = Platform.OS === 'android'

enum DrawerRoutes {
  shareCode = 'shareCode',
  gameDetails = 'gameDetails',
  leaderboard = 'leaderboard',
  cheatSheet = 'cheatSheet',
  show4ColorDeck = 'show4ColorDeck',
  hide4ColorDeck = 'hide4ColorDeck',
  sound = 'sound',
  support = 'support',
  // Admin/Teacher panel routes
  teacherControls = 'teacherControls',
  timingPanel = 'timingPanel',
  debugPanel = 'debugPanel',
  advisor = 'advisor'
}

interface IDrawerItem {
  route: DrawerRoutes
  title: string
  skipItem?: () => boolean
}

type GameOptionsDrawerState = {
  status: 'open' | 'close'
}

type GameOptionsDrawerActions = {
  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void
}

const openDrawer: GameOptionsDrawerActions['openDrawer'] = () => {
  rootStore?.analytics.setEvent(events.sideNav.drawer_opened, {
    isConvexEnabled: true
  })
  useGameOptionsDrawerStore.setState({ status: 'open' })
}

const closeDrawer: GameOptionsDrawerActions['closeDrawer'] = () => {
  rootStore?.analytics.setEvent(events.sideNav.drawer_closed, {
    isConvexEnabled: true
  })
  useGameOptionsDrawerStore.setState({ status: 'close' })
}

const toggleDrawer: GameOptionsDrawerActions['toggleDrawer'] = () => {
  const event =
    useGameOptionsDrawerStore.getState().status === 'open' ? events.sideNav.drawer_closed : events.sideNav.drawer_opened
  rootStore?.analytics.setEvent(event, {
    isConvexEnabled: true
  })
  useGameOptionsDrawerStore.setState((s) => ({ status: s.status === 'open' ? 'close' : 'open' }))
}

export const useGameOptionsDrawerStore = create<GameOptionsDrawerState & GameOptionsDrawerActions>(() => ({
  status: 'close',
  openDrawer,
  closeDrawer,
  toggleDrawer
}))

interface GameOptionsDrawerContentProps {
  onLeaveGame: () => void
  onOpenShareModal: () => void
  gameCode?: string
  gameName?: string
}

const GameOptionsDrawerContent = observer(
  ({ onLeaveGame, onOpenShareModal, gameCode, gameName }: GameOptionsDrawerContentProps) => {
    const store = useStore()
    const router = useRouter()
    const { gameId, userId } = useGameContext()
    const { open: openBottomModal } = useCheatSheet()
    const { open: openSupportFormBottomModal } = useSupportFormModal()
    const { game, myPlayer } = useGameState()
    const {
      enabled: advisorEnabled,
      setEnabled: setAdvisorEnabled,
      shouldShowToggle: shouldShowAdvisorToggle,
      clearFeedback
    } = useActionAdvisor({
      gameId: game?._id,
      handNumber: game?.handNumber,
      mode: game?.mode,
      userId: myPlayer?.userId
    })
    const adviceBeforePlay = useActionAdvisorStore((state) => state.adviceBeforePlay)
    const setAdviceBeforePlay = useActionAdvisorStore((state) => state.setAdviceBeforePlay)
    const resetWelcomeBudget = useActionAdvisorStore((state) => state.resetWelcomeBudget)

    // Which sub-view the drawer shows: the options menu, or the read-only game
    // settings panel (rendered over the table). Reset to the menu when the drawer closes.
    const [advisorMenuVisible, setAdvisorMenuVisible] = useState(false)
    const [view, setView] = useState<'menu' | 'settings'>('menu')
    const drawerStatus = useGameOptionsDrawerStore((s) => s.status)
    useEffect(() => {
      if (drawerStatus === 'close') {
        setView('menu')
      }
    }, [drawerStatus])

    const {
      showTeacherControls,
      showTimingPanel,
      showDebugPanel,
      toggleTeacherControls,
      toggleTimingPanel,
      toggleDebugPanel
    } = useGamePanelsStore()

    const { DEFAULT_GAME_NAME } = GAME_CONSTANTS.DAILY10_CONSTANTS
    const isFourColoredDeckEnabled = store.game?.isFourColoredDeckEnabled
    const isSoundEnabled = store.permissions?.isSoundEnabled
    const gameTitle =
      game?.mode === 'daily10' ? DEFAULT_GAME_NAME : gameName || store.game?.currentTournament?.name || 'Quick Game'
    const gameLabel = `${game?.mode === 'daily10' ? DEFAULT_GAME_NAME : game?.mode} GAME`.toUpperCase()

    const userRoles = store.session?.user?.userRoles ?? []
    const isAdmin = userRoles.includes('admin') || userRoles.includes('superadmin')
    const isTeacher = userRoles.includes('teacher')
    const canAccessTeacherControls = isAdmin || isTeacher
    const isTournament = game?.mode === 'mtt'

    const toggleDrawer = useThrottledCallback(() => useGameOptionsDrawerStore.getState().closeDrawer(), 250, {
      leading: true
    })

    const handleLeaveGame = useThrottledCallback(() => {
      const isCommunityGame = store.game.currentTournament?.accessControl === 'community'
      const notFinished = store.game.game?.gameState?.tag !== 'finished'
      const notObserver =
        !store.game.game ||
        !!(store.game.game?.players as Player[]).find(
          (player) => player.userId === store.session.user?._id && player.state.tag !== 'observer'
        )

      store.game.createLeaveGameSheet(isCommunityGame && notFinished && notObserver)
      onLeaveGame()
      setTimeout(toggleDrawer, 25)
    }, 100)

    const toggleSound = () => store.permissions.setIsSoundEnabled(!isSoundEnabled)

    const onOpenCheatSheet = useThrottledCallback(() => {
      openBottomModal()
      toggleDrawer()
    }, 250)

    const openSupportModal = () => {
      openSupportFormBottomModal()
      toggleDrawer()
    }

    const shareMessage = gameCode ? `${gameCode}: Join my game on Poker Power` : ''

    const handleShareCode = useCallback(async () => {
      if (!gameCode) return
      toggleDrawer()
      requestAnimationFrame(() => {
        setTimeout(() => {
          onOpenShareModal()
        }, 300)
      })
    }, [gameCode, toggleDrawer, onOpenShareModal])

    const useFourColor = store.game.isFourColoredDeckEnabled

    const drawerItems: IDrawerItem[] = [
      // Tournament Leaderboard / Tables used to live here. Both are now tabs
      // on the lobby (`/mtt/[tournamentId]?tab=leaderboard|tables`). The
      // side-nav Leave button (renamed to "Step Away" in tournament mode)
      // is the single point of egress from the in-game experience.

      // Share Code - only show for private games (games with codes)
      ...(game?.mode === 'private'
        ? [
            {
              route: DrawerRoutes.shareCode,
              title: 'Share Game Code'
            }
          ]
        : []),
      {
        route: DrawerRoutes.gameDetails,
        title: translate('drawer.gameDetails')
      },
      ...(game?.mode !== 'mtt'
        ? [
            {
              route: DrawerRoutes.leaderboard,
              title: translate('drawer.leaderboard')
            }
          ]
        : []),
      {
        route: DrawerRoutes.cheatSheet,
        title: translate('drawer.handRankings')
      },
      {
        route: isFourColoredDeckEnabled ? DrawerRoutes.hide4ColorDeck : DrawerRoutes.show4ColorDeck,
        title: translate(isFourColoredDeckEnabled ? 'drawer.fourColorHide' : 'drawer.fourColorShow')
      },
      {
        route: DrawerRoutes.sound,
        title: translate('drawer.sounds')
      },
      {
        route: DrawerRoutes.support,
        title: translate('drawer.support')
      },
      ...(canAccessTeacherControls
        ? [
            {
              route: DrawerRoutes.teacherControls,
              title: 'Teacher Controls'
            }
          ]
        : []),
      ...(isAdmin
        ? [
            {
              route: DrawerRoutes.timingPanel,
              title: 'Timing Panel'
            }
          ]
        : []),
      ...(isAdmin
        ? [
            {
              route: DrawerRoutes.debugPanel,
              title: 'Debug Data'
            }
          ]
        : []),
      // PRODUCT DECISION: the advisor drawer row is open to ALL players in
      // supported modes. `shouldShowAdvisorToggle` (from the availability
      // gates) still hides it in unsupported modes, and the avatar is gated
      // by the same availability logic. Only the reset link inside the row
      // stays admin-only (see its render branch below).
      ...(shouldShowAdvisorToggle
        ? [
            {
              route: DrawerRoutes.advisor,
              title: ACTION_ADVISOR_COPY.drawerLabel
            }
          ]
        : [])
    ]

    const onPressDrawerItem = (item: IDrawerItem) => {
      switch (item.route) {
        case DrawerRoutes.shareCode:
          handleShareCode()
          break

        case DrawerRoutes.gameDetails:
          // Swap to the read-only settings panel in place — keep the drawer open
          // so the table stays visible behind it.
          setView('settings')
          break

        case DrawerRoutes.leaderboard:
          toggleDrawer()
          router.navigate({
            pathname: '/(root)/game/leaderboard',
            params: gameId && userId ? { gameId, userId } : undefined
          })
          break

        case DrawerRoutes.cheatSheet:
          onOpenCheatSheet()
          break

        case DrawerRoutes.support:
          openSupportModal()
          break
      }
    }

    const handleCopyCode = useCallback(async () => {
      if (!gameCode) return
      await Clipboard.setStringAsync(gameCode)
      useToastStore.getState().enqueueToast({
        kind: 'success',
        message: `Code copied: ${gameCode}`
      })
    }, [gameCode])

    // Read-only game settings panel — rendered with the same drawer chrome so the
    // table stays visible behind it.
    if (view === 'settings') {
      return (
        <Pressable onPress={toggleDrawer} style={styles.drawerContainer}>
          <View style={[styles.drawerMenuContainer, { paddingTop: isAndroid ? 25 : 0 }]}>
            <SafeAreaView style={{ backgroundColor: 'transparent', flex: 1, justifyContent: 'space-between' }}>
              <GameSettingsPanel onBack={() => setView('menu')} />
            </SafeAreaView>
          </View>
        </Pressable>
      )
    }

    return (
      <>
        <Pressable onPress={toggleDrawer} style={styles.drawerContainer}>
          <View style={[styles.drawerMenuContainer, { paddingTop: isAndroid ? 25 : 0 }]}>
            <SafeAreaView style={{ backgroundColor: 'transparent', flex: 1, justifyContent: 'space-between' }}>
              {/* HEADER SECTION */}
              <View style={styles.headerSection}>
                <DS.Text variant="Caption/Bold" color="Global/Black" style={styles.practiceGameLabel}>
                  {gameLabel}
                </DS.Text>
                {gameCode && game?.mode === 'private' && (
                  <TouchableOpacity onPress={handleCopyCode} style={styles.codeContainerTop}>
                    <DS.Frame row alignItems="center" justifyContent="space-around">
                      <DS.Text variant="Body Medium/Medium" color="Brand/Maroon" style={styles.codeTextTop}>
                        {gameCode}
                      </DS.Text>
                      <DS.Frame style={{ marginLeft: 12 }}>
                        <DS.Icon name="Copy" size={20} color="Brand/Dark Grey" />
                      </DS.Frame>
                    </DS.Frame>
                  </TouchableOpacity>
                )}
              </View>

              {game?.mode === 'mtt' && gameName && (
                <DS.Text
                  variant="Heading 3/Medium"
                  color="Global/Black"
                  style={{ textAlign: 'right', marginBottom: 8 }}
                >
                  {gameName}
                </DS.Text>
              )}

              {/* DRAWER ITEMS */}
              <ScrollView
                style={styles.drawerItemsSection}
                contentContainerStyle={styles.drawerItemsContent}
                showsVerticalScrollIndicator={false}
              >
                <View style={styles.drawerItemsContainer}>
                  {drawerItems.map((item) => {
                    if (item.skipItem?.()) {
                      return null
                    }

                    if ([DrawerRoutes.show4ColorDeck, DrawerRoutes.hide4ColorDeck].includes(item.route)) {
                      return <DrawerCardColors key={item.title} useFourColor={useFourColor} />
                    }

                    if (item.route === DrawerRoutes.sound) {
                      return (
                        <DrawerItem key={item.title} text={item.title}>
                          <Switch
                            onValueChange={() => toggleSound()}
                            style={{ marginLeft: 16 }}
                            value={isSoundEnabled}
                            color={color.palette.maroon}
                          />
                        </DrawerItem>
                      )
                    }

                    if (item.route === DrawerRoutes.advisor) {
                      // Inline dropdown for the three advisor states (Paper's
                      // Menu portal does not play well inside the drawer-layout
                      // drawer, so this is a plain absolute-positioned panel).
                      // Off = advisor hidden; Beginner = advice before each
                      // play; Intermediate = feedback after each play.
                      // PRODUCT DECISION: the drawer level owns WHAT the
                      // advisor does; the avatar tap (feedbackEnabled) is the
                      // quick on/off mute while playing — players want to
                      // silence it mid-session without losing their level.
                      // The mute gates everything (advice AND grades:
                      // advisorContext is only composed when unmuted), so a
                      // muted advisor shows no pre-play advice either. That
                      // layering is intentional; the welcome bubble tells the
                      // player to tap the avatar to turn it on.
                      // Row layout mirrors the Card Colors row (DS.Frame,
                      // flex-end, label + control side by side).
                      const levelOptions = [
                        { value: 'beginner', label: ACTION_ADVISOR_COPY.advisorBeginner },
                        { value: 'intermediate', label: ACTION_ADVISOR_COPY.advisorIntermediate },
                        { value: 'off', label: ACTION_ADVISOR_COPY.advisorOff }
                      ] as const
                      const currentLevel = advisorEnabled ? (adviceBeforePlay ? 'beginner' : 'intermediate') : 'off'
                      const currentLabel =
                        levelOptions.find((option) => option.value === currentLevel)?.label ??
                        ACTION_ADVISOR_COPY.advisorOff
                      const selectLevel = (value: (typeof levelOptions)[number]['value']) => {
                        // Analytics: from → to, with the new level stamped as the
                        // coachingLevel metadata (the level the player now has).
                        rootStore?.analytics.setEvent(events.advisor.level_changed, {
                          from: currentLevel,
                          to: value,
                          coachingLevel: value,
                          gameMode: game?.mode
                        })
                        if (value === 'beginner') {
                          setAdvisorEnabled(true)
                          setAdviceBeforePlay(true)
                        } else if (value === 'intermediate') {
                          setAdvisorEnabled(true)
                          setAdviceBeforePlay(false)
                        } else {
                          setAdvisorEnabled(false)
                          clearFeedback()
                        }
                        setAdvisorMenuVisible(false)
                      }
                      // Stacked layout: label above the picker, admin reset
                      // link below it — all right-aligned. The picker pill hugs
                      // its content, capped at 75% of the row width; a level
                      // name longer than that clips on a single line (by
                      // design). The panel is likewise capped at the row width
                      // so it never spills past the drawer's left edge.
                      return (
                        <DS.Frame key={item.title} style={styles.advisorDropdownRow}>
                          <DS.Text variant="Body Medium/Regular" allowFontScaling={false}>
                            {item.title}
                          </DS.Text>
                          <View style={styles.advisorDropdownWrap}>
                            <TouchableOpacity
                              onPress={() => setAdvisorMenuVisible(!advisorMenuVisible)}
                              accessibilityRole="button"
                              accessibilityState={{ expanded: advisorMenuVisible }}
                              accessibilityLabel={`${item.title}: ${currentLabel}`}
                              style={styles.advisorDropdownAnchor}
                            >
                              <DS.Text variant="Body Small/Medium" allowFontScaling={false} numberOfLines={1}>
                                {currentLabel}
                              </DS.Text>
                              <DS.Icon name="CaretDown" size={10} />
                            </TouchableOpacity>
                            {advisorMenuVisible ? (
                              <View style={styles.advisorDropdownPanel}>
                                {levelOptions.map(({ value, label }) => (
                                  <TouchableOpacity
                                    key={value}
                                    onPress={() => selectLevel(value)}
                                    accessibilityRole="menuitem"
                                    accessibilityState={{ selected: currentLevel === value }}
                                    style={styles.advisorDropdownOption}
                                  >
                                    <DS.Text
                                      variant={currentLevel === value ? 'Body Small/Medium' : 'Body Small/Regular'}
                                      allowFontScaling={false}
                                      numberOfLines={1}
                                      style={styles.advisorDropdownOptionText}
                                    >
                                      {label}
                                    </DS.Text>
                                    {currentLevel === value ? <DS.Icon name="CheckThick" size={12} /> : null}
                                  </TouchableOpacity>
                                ))}
                              </View>
                            ) : null}
                          </View>
                          {/* Admin tool: reset the welcome-bubble session budget
                              so the introduction flow can be re-tested. The
                              advisor drawer row is open to all players, so
                              this link carries its own admin gate. */}
                          {isAdmin && (
                            <TouchableOpacity
                              onPress={() => {
                                resetWelcomeBudget()
                                setAdvisorMenuVisible(false)
                              }}
                              accessibilityRole="button"
                              accessibilityLabel={ACTION_ADVISOR_COPY.advisorResetWelcome}
                              hitSlop={6}
                            >
                              <DS.Text variant="Caption/Regular" color="Brand/Maroon" allowFontScaling={false}>
                                {ACTION_ADVISOR_COPY.advisorResetWelcome}
                              </DS.Text>
                            </TouchableOpacity>
                          )}
                        </DS.Frame>
                      )
                    }

                    // Teacher Controls - tappable item
                    if (item.route === DrawerRoutes.teacherControls) {
                      return (
                        <DrawerItem
                          key={item.title}
                          text={showTeacherControls ? '✓ Teacher Controls' : 'Teacher Controls'}
                          onPress={() => {
                            toggleTeacherControls()
                            toggleDrawer()
                          }}
                        />
                      )
                    }

                    // Timing Panel - tappable item
                    if (item.route === DrawerRoutes.timingPanel) {
                      return (
                        <DrawerItem
                          key={item.title}
                          text={showTimingPanel ? '✓ Timing Panel' : 'Timing Panel'}
                          onPress={() => {
                            toggleTimingPanel()
                            toggleDrawer()
                          }}
                        />
                      )
                    }

                    // Debug Panel - tappable item
                    if (item.route === DrawerRoutes.debugPanel) {
                      return (
                        <DrawerItem
                          key={item.title}
                          text={showDebugPanel ? '✓ Debug Data' : 'Debug Data'}
                          onPress={() => {
                            toggleDebugPanel()
                            toggleDrawer()
                          }}
                        />
                      )
                    }

                    // Add appropriate icons
                    const showArrow = item.route === DrawerRoutes.cheatSheet
                    const showShareIcon = item.route === DrawerRoutes.shareCode

                    return (
                      <DrawerItem key={item.title} text={item.title} onPress={() => onPressDrawerItem(item)}>
                        {showArrow && <DS.Icon name="Key" size={16} color="Global/Black" />}
                        {showShareIcon && <DS.Icon name="Arrow--top-right" size={16} color="Global/Black" />}
                      </DrawerItem>
                    )
                  })}
                </View>
              </ScrollView>

              {/* BOTTOM SECTION */}
              <View style={styles.bottomSection}>
                {/* USERNAME */}
                {/* {username && (
              <View style={styles.usernameContainer}>
                <Text style={styles.usernameText}>{username}</Text>
              </View>
            )} */}

                {/*
                  LEAVE GAME BUTTON
                  In tournament mode the user is leaving a single table but
                  remains registered in the tournament — "Step Away" signals
                  that they can return / spectate. Non-tournament modes are a
                  hard exit, so keep the existing "Leave Game" label.
                */}
                <View style={styles.leaveButtonContainer}>
                  <Button
                    text={isTournament ? translate('teacherOptions.stepAway') : translate('teacherOptions.leaveGame')}
                    size="large"
                    onPress={handleLeaveGame}
                  />
                </View>

                {/* VERSION TEXT */}
                <Text style={styles.appVersionText}>{BUILD_VERSION_FORMATTED_TEXT}</Text>
              </View>
            </SafeAreaView>
          </View>
        </Pressable>
      </>
    )
  }
)

interface GameOptionsDrawerProps {
  children: React.ReactNode
  onLeaveGame: () => void
  gameCode?: string
  gameName?: string
}

export const GameOptionsDrawer = observer(({ children, onLeaveGame, gameCode, gameName }: GameOptionsDrawerProps) => {
  const { status, openDrawer, closeDrawer } = useGameOptionsDrawerStore()
  const [showShareModal, setShowShareModal] = useState(false)
  // Cap the drawer at 80% of the screen so it never swallows the table on
  // narrow devices. The inner menu is fixed at 280px (350 on web); the cap
  // only bites when 80% of the window is narrower than that.
  const { width: windowWidth } = useWindowDimensions()
  const drawerWidth = Math.min(Platform.OS === 'web' ? 350 : 280, Math.round(windowWidth * 0.8))

  const handleOpenShareModal = useCallback(() => setShowShareModal(true), [])
  const handleCloseShareModal = useCallback(() => setShowShareModal(false), [])

  return (
    <>
      <Drawer
        open={status === 'open'}
        onOpen={openDrawer}
        onClose={closeDrawer}
        renderDrawerContent={() => (
          <GameOptionsDrawerContent
            onLeaveGame={onLeaveGame}
            onOpenShareModal={handleOpenShareModal}
            gameCode={gameCode}
            gameName={gameName}
          />
        )}
        drawerType="front"
        drawerPosition="right"
        drawerStyle={{
          zIndex: 1000,
          backgroundColor: 'transparent',
          width: drawerWidth
        }}
        swipeEnabled={false}
        overlayStyle={{
          backgroundColor: 'rgba(18, 18, 18, .2)'
        }}
      >
        {children}
      </Drawer>

      <GameModal visible={!!gameCode && showShareModal} onClose={handleCloseShareModal}>
        <ShareCodeSheet code={gameCode ?? ''} onClose={handleCloseShareModal} showQRButton={true} />
      </GameModal>
    </>
  )
})

const styles = StyleSheet.create({
  drawerContainer: {
    flex: 1,
    zIndex: 1,
    width: '100%',
    alignItems: 'flex-end'
  },
  drawerMenuContainer: {
    zIndex: 2,
    height: '100%',
    backgroundColor: color.palette.cream,
    justifyContent: 'flex-end',
    // Fill the (width-capped) Drawer — the outer drawer's width is already
    // min(280 native / 350 web, 80% of window), so this just tracks it. The
    // 80% cap wins over the old 300px web min on very narrow windows.
    width: '100%',
    paddingHorizontal: 24
  },
  headerSection: {
    paddingTop: 60,
    paddingBottom: 10,
    gap: 4,
    alignItems: 'flex-end',
    justifyContent: 'flex-end'
  },
  drawerItemsSection: {
    flex: 1
  },
  drawerItemsContent: {
    flexGrow: 1
  },
  bottomSection: {
    paddingBottom: 24
  },
  practiceGameLabel: {
    fontSize: 12,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 8
  },
  // Advisor row: stacked column, right-aligned — the 'Advisor' label sits
  // above the picker, the admin reset link below it. The dropdown panel is a
  // plain absolute-positioned list (Paper's Menu portal does not open inside
  // the drawer-layout drawer).
  advisorDropdownRow: {
    alignItems: 'flex-end',
    gap: DS.spacing[4],
    paddingVertical: 8
  },
  // Wrapper stretches to the row width so the anchor pill and the upward
  // panel both align to the drawer's right edge.
  advisorDropdownWrap: {
    alignSelf: 'stretch'
  },
  // Anchor mimics the segment control's selected segment: white pill on a
  // hairline light-grey border. Hugs its content (compact for 'Off' /
  // 'Feedback Only'), capped at 75% of the row width; a longer level name
  // ('Advice + Feedback') clips on a single line — accepted per
  // design.
  advisorDropdownAnchor: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
    backgroundColor: color.palette.white,
    borderWidth: 1,
    borderColor: color.palette.lightGrey,
    borderRadius: 16,
    alignSelf: 'flex-end',
    maxWidth: '75%',
    paddingHorizontal: 10,
    paddingVertical: 6
  },
  // Opens UPWARD: the advisor row is the last menu item, so a downward panel
  // extends past the ScrollView's frame and gets clipped (the missing bottom
  // border). Upward overlays the items above instead, which paint below it.
  // Capped at the row width: the widest option can exceed the drawer's inner
  // width, and without the cap the right-aligned panel spills past the
  // drawer's left edge and gets clipped.
  advisorDropdownPanel: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    marginBottom: 6,
    zIndex: 10,
    backgroundColor: color.palette.white,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: color.palette.lightGrey,
    padding: 4,
    gap: 2,
    minWidth: 150,
    maxWidth: '100%'
  },
  advisorDropdownOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 10
  },
  // Lets the option text yield to the check icon and ellipsize when the panel
  // hits its width cap, instead of overflowing past the drawer's left edge.
  advisorDropdownOptionText: {
    flexShrink: 1
  },
  codeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4
  },
  gameTitle: {
    fontSize: 32,
    lineHeight: 40,
    fontFamily: fontFamily.GTAmericaStandardRegular,
    color: color.palette.black,
    textAlign: 'right'
  },
  usernameContainer: {
    paddingHorizontal: 0,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: color.palette.lightGrey
  },
  usernameText: {
    fontSize: 16,
    fontFamily: fontFamily.GTAmericaStandardRegular,
    color: color.palette.black
  },
  leaveButtonContainer: {
    paddingVertical: 16,
    gap: 8
  },
  appVersionText: {
    fontSize: 12,
    fontFamily: fontFamily.GTAmericaStandardRegular,
    color: 'grey',
    marginTop: 8,
    marginBottom: 40,
    alignSelf: 'flex-end'
  },
  drawerItemsContainer: {
    paddingTop: DS.spacing[20],
    backgroundColor: 'transparent',
    borderTopColor: color.palette.lightGrey,
    borderTopWidth: 1,
    paddingBottom: DS.spacing[20],
    gap: DS.spacing[20]
  },
  codeContainerTop: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2
  },
  codeLabelTop: {
    marginBottom: 4
  },
  codeTextTop: {
    letterSpacing: 2
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center'
  },
  modalContent: {
    width: '90%',
    maxWidth: 500,
    backgroundColor: DS.palette['Brand/Cream'],
    borderRadius: 16,
    overflow: 'hidden'
  }
})
